/**
 * ChallengeSessionsList — sortable challenge-sessions list with a
 * per-row "Day label" text input.
 *
 * The challenge form's `sessions` array is shaped:
 *   [ { [session_id]: [challenge_day, session_repetition] }, … ]
 *
 * That's the wire format v1 ships to the API (kept verbatim for
 * compatibility), so this component reads + writes that shape
 * directly. `session_data` is a parallel array of full session
 * objects (session_id, session_name, …) used for label lookup.
 *
 * Repetition isn't editable in v1 either — the form stores
 * whatever the API returned. We surface it as a read-only chip
 * when present so editors can see it without an extra UI control.
 */
import React, { useCallback, useMemo } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

const DragGlyph = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 14, height: 14 }}>
    <circle cx="9" cy="6" r="0.5" />
    <circle cx="9" cy="12" r="0.5" />
    <circle cx="9" cy="18" r="0.5" />
    <circle cx="15" cy="6" r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

function SortableRow({ id, label, dayValue, repetition, index, onDayChange }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center">
      <div
        className="flex items-center"
        style={{
          flex: 1,
          gap: 10,
          padding: '8px 12px',
          background: '#FAF7F1',
          border: '1px solid #E8E0CF',
          borderRadius: 8,
          marginBottom: 6,
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
          minWidth: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#D6CCAE';
          e.currentTarget.style.boxShadow = '0 1px 3px #2C3E5012';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#E8E0CF';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Drag handle */}
        <button
          type="button"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          className="grid place-items-center"
          style={{
            width: 22,
            height: 22,
            border: 0,
            background: 'transparent',
            color: '#9CA1A8',
            cursor: 'grab',
            touchAction: 'none',
            flexShrink: 0,
          }}
        >
          {DragGlyph}
        </button>

        {/* Index */}
        <span
          className="font-mono text-text-muted"
          style={{ fontSize: 11, minWidth: 22, textAlign: 'right' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        {/* Label */}
        <span
          className="text-navy"
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {label || `Session ${id}`}
        </span>

        {/* Read-only repetition pill (not editable in v1 either) */}
        {repetition && Number(repetition) > 1 && (
          <span
            className="font-condensed font-bold uppercase"
            style={{
              fontSize: 10,
              letterSpacing: '0.08em',
              padding: '2px 7px',
              borderRadius: 999,
              background: '#FAF4EA',
              border: '1px solid #E8E0CF',
              color: '#9CA1A8',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            title="Times the session repeats during the challenge"
          >
            ×{repetition}
          </span>
        )}

        {/* Day label input — names match v1 so handleChallengesDay
            in the parent form keeps working unchanged. */}
        <input
          name={`days-${id}`}
          value={dayValue || ''}
          onChange={onDayChange}
          placeholder="Day"
          className="font-mono"
          style={{
            width: 110,
            padding: '6px 10px',
            fontSize: 12.5,
            border: '1px solid #E8E0CF',
            borderRadius: 4,
            background: '#FAF4EA',
            color: '#2C3E50',
            outline: 'none',
            textAlign: 'center',
            flexShrink: 0,
          }}
        />
      </div>
    </div>
  );
}

export default function ChallengeSessionsList({
  items,         // [{[session_id]: [day, repetition]}, ...]
  sessionData,   // [{ session_id, session_name, ... }, ...]
  onSortEnd,     // ({ oldIndex, newIndex }) => void
  handleChallengesDay, // (e) => void — v1 handler signature
  emptyText = 'No sessions yet.',
}) {
  // Stable string IDs for dnd-kit (it dislikes numeric ids).
  const ids = useMemo(
    () => (items || []).map((row) => String(Object.keys(row)[0])),
    [items],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = ids.findIndex((id) => id === active.id);
      const newIndex = ids.findIndex((id) => id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      // v1's SortableSessions calls onSortEnd with { oldIndex,
      // newIndex } — preserve the same callback signature so the
      // parent's handler is reusable.
      // We don't mutate here — the parent owns the order array and
      // is responsible for applying the move + setState (same as v1).
      if (onSortEnd) onSortEnd({ oldIndex, newIndex });
    },
    [ids, onSortEnd],
  );

  if (!items || items.length === 0) {
    return (
      <div
        className="text-text-muted"
        style={{
          padding: 16,
          fontSize: 13,
          textAlign: 'center',
          background: '#FAF7F1',
          border: '1px dashed #E8E0CF',
          borderRadius: 8,
        }}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((row, index) => {
          const id = Object.keys(row)[0];
          const [dayValue, repetition] = row[id] || ['', ''];
          const session = (sessionData || []).find(
            (s) => Number(s.session_id) === Number(id),
          );
          return (
            <SortableRow
              key={id}
              id={id}
              index={index}
              label={session?.session_name}
              dayValue={dayValue}
              repetition={repetition}
              onDayChange={handleChallengesDay}
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
}
